import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiPlus, mdiDelete, mdiContentSave } from '@mdi/js';
import { adminTheme } from '../styles/adminTheme';

const Container = styled.div`
  margin-top: 16px;
`;

const Title = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: ${adminTheme.colors.text};
  margin-bottom: 8px;
`;

const Hint = styled.div`
  font-size: 11px;
  color: ${adminTheme.colors.textMuted};
  margin-bottom: 10px;
  line-height: 1.4;
`;

const UnitBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 6px;
`;

const UnitGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UnitLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${adminTheme.colors.textSecondary};
`;

const UnitSelect = styled.select`
  padding: 3px 6px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 3px;
  font-size: 11px;
  color: ${adminTheme.colors.text};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary};
  }
`;

const TableWrap = styled.div`
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 6px;
  overflow: hidden;
  max-height: 400px;
  overflow-y: auto;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    position: sticky;
    top: 0;
    background: #fafbfc;
    padding: 8px 6px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: ${adminTheme.colors.textSecondary};
    border-bottom: 2px solid ${adminTheme.colors.border};
    white-space: nowrap;
  }

  td {
    padding: 2px 4px;
    border-bottom: 1px solid ${adminTheme.colors.border};
  }

  tr:nth-child(even) {
    background: ${adminTheme.colors.rowAlt};
  }
`;

const CellInput = styled.input`
  width: 100%;
  min-width: 70px;
  padding: 5px 6px;
  border: 1px solid transparent;
  border-radius: 3px;
  font-size: 12px;
  color: ${adminTheme.colors.text};
  background: transparent;
  text-align: right;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary};
    background: white;
  }

  &:hover {
    border-color: ${adminTheme.colors.border};
  }
`;

const DeleteRowBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 3px;
  color: ${adminTheme.colors.textMuted};
  display: flex;

  &:hover {
    color: ${adminTheme.colors.danger};
    background: #fce8e6;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${p => p.$primary ? adminTheme.colors.primary : adminTheme.colors.border};
  background: ${p => p.$primary ? adminTheme.colors.primary : 'white'};
  color: ${p => p.$primary ? 'white' : adminTheme.colors.text};

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ═══════════════════════════════════════════════════════
// Unit system — defines available units and conversion
// to solver internal units (l/min, kPa, °C, kW)
// ═══════════════════════════════════════════════════════

type Quantity = 'flow' | 'pressure' | 'temperature' | 'power';

interface UnitOption {
  id: string;
  label: string;
  toSolver: (v: number) => number;   // user input → solver unit
  fromSolver: (v: number) => number; // solver unit → display unit
}

const UNIT_OPTIONS: Record<Quantity, UnitOption[]> = {
  flow: [
    { id: 'lpm',  label: 'l/min',  toSolver: v => v,           fromSolver: v => v },
    { id: 'm3h',  label: 'm³/h',   toSolver: v => v * 1000/60, fromSolver: v => v * 60/1000 },
    { id: 'gpm',  label: 'GPM',    toSolver: v => v * 3.78541,  fromSolver: v => v / 3.78541 },
  ],
  pressure: [
    { id: 'kpa',  label: 'kPa',    toSolver: v => v,        fromSolver: v => v },
    { id: 'bar',  label: 'bar',    toSolver: v => v * 100,  fromSolver: v => v / 100 },
    { id: 'mbar', label: 'mbar',   toSolver: v => v * 0.1,  fromSolver: v => v * 10 },
    { id: 'psi',  label: 'psi',    toSolver: v => v * 6.89476, fromSolver: v => v / 6.89476 },
    { id: 'pa',   label: 'Pa',     toSolver: v => v / 1000, fromSolver: v => v * 1000 },
  ],
  temperature: [
    { id: 'c',  label: '°C', toSolver: v => v,                    fromSolver: v => v },
    { id: 'f',  label: '°F', toSolver: v => (v - 32) * 5/9,      fromSolver: v => v * 9/5 + 32 },
    { id: 'k',  label: 'K',  toSolver: v => v - 273.15,           fromSolver: v => v + 273.15 },
  ],
  power: [
    { id: 'kw', label: 'kW', toSolver: v => v,            fromSolver: v => v },
    { id: 'w',  label: 'W',  toSolver: v => v / 1000,     fromSolver: v => v * 1000 },
    { id: 'hp', label: 'HP', toSolver: v => v * 0.7457,   fromSolver: v => v / 0.7457 },
  ],
};

// Which physical quantity each column key maps to
const KEY_QUANTITY: Record<string, Quantity> = {
  flow_rate: 'flow',
  volume_flow: 'flow',
  pressure_rise: 'pressure',
  pressure_drop: 'pressure',
  ambient_temp: 'temperature',
  inlet_temp: 'temperature',
  outlet_temp: 'temperature',
  thermal_output: 'power',
};

// ═══════════════════════════════════════════════════════
// Column definitions per component type
// ═══════════════════════════════════════════════════════

interface ColumnDef {
  key: string;
  label: string;
  quantity: Quantity;
}

const PUMP_COLUMNS: ColumnDef[] = [
  { key: 'flow_rate', label: 'Flow Rate', quantity: 'flow' },
  { key: 'pressure_rise', label: 'Pressure Rise', quantity: 'pressure' },
];

const RESTRICTION_COLUMNS: ColumnDef[] = [
  { key: 'flow_rate', label: 'Flow Rate', quantity: 'flow' },
  { key: 'pressure_drop', label: 'Pressure Drop', quantity: 'pressure' },
];

const COOLING_COLUMNS: ColumnDef[] = [
  { key: 'volume_flow', label: 'Flow Rate', quantity: 'flow' },
  { key: 'ambient_temp', label: 'Ambient T', quantity: 'temperature' },
  { key: 'inlet_temp', label: 'Inlet T', quantity: 'temperature' },
  { key: 'outlet_temp', label: 'Outlet T', quantity: 'temperature' },
  { key: 'thermal_output', label: 'Thermal Output', quantity: 'power' },
  { key: 'pressure_drop', label: 'Pressure Drop', quantity: 'pressure' },
];

function getColumns(componentType: string): ColumnDef[] {
  switch (componentType) {
    case 'pump': return PUMP_COLUMNS;
    case 'cooling_package': return COOLING_COLUMNS;
    case 'restriction':
    case 'heat_source':
      return RESTRICTION_COLUMNS;
    default: return RESTRICTION_COLUMNS;
  }
}

function getCurveType(componentType: string): string {
  return componentType === 'cooling_package' ? 'cooling_performance' : 'flow_vs_dp';
}

function emptyRow(columns: ColumnDef[]): Record<string, number> {
  const row: Record<string, number> = {};
  columns.forEach(c => { row[c.key] = 0; });
  return row;
}

// Which quantity groups are needed for a set of columns
function getRequiredQuantities(columns: ColumnDef[]): Quantity[] {
  const set = new Set<Quantity>();
  columns.forEach(c => set.add(c.quantity));
  return Array.from(set);
}

interface ManualDataEditorProps {
  componentType: string;
  data: Record<string, number>[];
  onSave: (curveType: string, data: Record<string, number>[]) => Promise<void>;
  saving?: boolean;
}

export const ManualDataEditor: React.FC<ManualDataEditorProps> = ({
  componentType,
  data,
  onSave,
  saving,
}) => {
  const columns = getColumns(componentType);
  const quantities = useMemo(() => getRequiredQuantities(columns), [columns]);

  // Selected unit per quantity (default: first = solver unit)
  const [units, setUnits] = useState<Record<Quantity, string>>(() => {
    const init: Record<string, string> = {};
    quantities.forEach(q => { init[q] = UNIT_OPTIONS[q][0].id; });
    return init as Record<Quantity, string>;
  });

  // Rows hold values in the CURRENTLY SELECTED display units
  const [rows, setRows] = useState<Record<string, number>[]>(() => {
    if (data.length === 0) return [emptyRow(columns)];
    // Data from DB is in solver units — convert to initial display units (which are solver units by default)
    return data.map(r => ({ ...r }));
  });

  const getUnitOption = useCallback((quantity: Quantity): UnitOption => {
    const unitId = units[quantity];
    return UNIT_OPTIONS[quantity].find(u => u.id === unitId) || UNIT_OPTIONS[quantity][0];
  }, [units]);

  // When user changes unit for a quantity, convert all existing row values
  const handleUnitChange = useCallback((quantity: Quantity, newUnitId: string) => {
    const oldUnit = UNIT_OPTIONS[quantity].find(u => u.id === units[quantity])!;
    const newUnit = UNIT_OPTIONS[quantity].find(u => u.id === newUnitId)!;

    // Convert rows: old display → solver → new display
    setRows(prev => prev.map(row => {
      const next = { ...row };
      columns.forEach(col => {
        if (col.quantity === quantity) {
          const solverVal = oldUnit.toSolver(next[col.key] || 0);
          next[col.key] = parseFloat(newUnit.fromSolver(solverVal).toFixed(6));
        }
      });
      return next;
    }));

    setUnits(prev => ({ ...prev, [quantity]: newUnitId }));
  }, [units, columns]);

  const handleCellChange = useCallback((rowIdx: number, key: string, value: string) => {
    setRows(prev => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [key]: value === '' ? 0 : parseFloat(value) || 0 };
      return next;
    });
  }, []);

  const addRow = useCallback(() => {
    setRows(prev => {
      const last = prev[prev.length - 1];
      const newRow = last ? { ...last } : emptyRow(columns);
      return [...prev, newRow];
    });
  }, [columns]);

  const removeRow = useCallback((idx: number) => {
    setRows(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));
  }, []);

  const handleSave = useCallback(async () => {
    // Convert all display values → solver units before saving
    const solverRows = rows
      .filter(row => columns.some(c => row[c.key] !== 0))
      .map(row => {
        const converted: Record<string, number> = {};
        columns.forEach(col => {
          const unit = getUnitOption(col.quantity);
          converted[col.key] = parseFloat(unit.toSolver(row[col.key] || 0).toFixed(6));
        });
        return converted;
      });

    if (solverRows.length === 0) return;
    await onSave(getCurveType(componentType), solverRows);
  }, [rows, columns, componentType, onSave, getUnitOption]);

  const isCooling = componentType === 'cooling_package';

  // Build column header showing current unit
  const getColumnUnit = (col: ColumnDef): string => {
    const unitOpt = getUnitOption(col.quantity);
    return unitOpt.label;
  };

  const quantityLabels: Record<Quantity, string> = {
    flow: 'Flow',
    pressure: 'Pressure',
    temperature: 'Temperature',
    power: 'Power',
  };

  return (
    <Container>
      <Title>Manual Data Entry</Title>
      <Hint>
        {isCooling
          ? 'Enter operating points for different flow rates, ambient and inlet temperatures. The solver interpolates between points.'
          : `Enter flow rate vs ${componentType === 'pump' ? 'pressure rise' : 'pressure drop'} points. The solver interpolates between points.`}
      </Hint>

      <UnitBar>
        {quantities.map(q => (
          <UnitGroup key={q}>
            <UnitLabel>{quantityLabels[q]}:</UnitLabel>
            <UnitSelect
              value={units[q]}
              onChange={e => handleUnitChange(q, e.target.value)}
            >
              {UNIT_OPTIONS[q].map(u => (
                <option key={u.id} value={u.id}>{u.label}</option>
              ))}
            </UnitSelect>
          </UnitGroup>
        ))}
      </UnitBar>

      <TableWrap>
        <table>
          <thead>
            <tr>
              <th style={{ width: 30 }}>#</th>
              {columns.map(c => (
                <th key={c.key}>{c.label} [{getColumnUnit(c)}]</th>
              ))}
              <th style={{ width: 32 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td style={{ fontSize: 10, color: adminTheme.colors.textMuted, textAlign: 'center' }}>
                  {i + 1}
                </td>
                {columns.map(c => (
                  <td key={c.key}>
                    <CellInput
                      type="number"
                      step="any"
                      value={row[c.key] ?? ''}
                      onChange={e => handleCellChange(i, c.key, e.target.value)}
                    />
                  </td>
                ))}
                <td>
                  <DeleteRowBtn onClick={() => removeRow(i)} title="Remove row">
                    <Icon path={mdiDelete} size={0.5} />
                  </DeleteRowBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>

      <ButtonRow>
        <Btn onClick={addRow}>
          <Icon path={mdiPlus} size={0.55} />
          Add Row
        </Btn>
        <Btn $primary onClick={handleSave} disabled={saving}>
          <Icon path={mdiContentSave} size={0.55} />
          {saving ? 'Saving...' : 'Save Points'}
        </Btn>
      </ButtonRow>
    </Container>
  );
};
